import React, { useState } from 'react';
import { UploadSimple, SpinnerGap } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { uploadOfficialAdhesionKit } from '../../services/api';

export const OfficialKitUploadDialog = ({ profile, onClose, onUploaded }) => {
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [comment, setComment] = useState('');
  const type = profile.kit_type;
  const requiresComment = !!profile.current;
  const missingComment = requiresComment && !comment.trim();
  const selectFile = (selected) => {
    setError(''); setFile(null);
    if (!selected) return;
    if (!selected.name.toLowerCase().endsWith('.pdf')) { setError('Sélectionnez un fichier PDF.'); return; }
    if (selected.size > 10 * 1024 * 1024) { setError('Le PDF ne doit pas dépasser 10 Mo.'); return; }
    if (!selected.size) { setError('Le fichier est vide.'); return; }
    setFile(selected);
  };
  const save = async () => {
    if (!file || saving || missingComment) return;
    setSaving(true); setError('');
    try {
      await uploadOfficialAdhesionKit(type, file, comment);
      toast.success(<span data-testid={`official-kit-upload-success-${type}`}>Le nouveau kit officiel est en vigueur.</span>);
      await onUploaded();
    } catch (e) {
      const detail = e.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Impossible d’importer ce PDF. Réessayez.');
    } finally { setSaving(false); }
  };
  return <Dialog open onOpenChange={(open) => { if (!open && !saving) onClose(); }}>
    <DialogContent className="w-[calc(100%_-_2rem)] max-w-lg" data-testid={`official-kit-upload-dialog-${type}`}>
      <DialogHeader>
        <DialogTitle data-testid={`official-kit-upload-title-${type}`}>{profile.current ? 'Remplacer le kit officiel' : 'Importer le kit officiel'}</DialogTitle>
        <DialogDescription data-testid={`official-kit-upload-profile-${type}`}>{profile.label}</DialogDescription>
      </DialogHeader>
      <div className="space-y-3 min-w-0">
        <Label htmlFor={`official-file-${type}`}>Document PDF</Label>
        <Input id={`official-file-${type}`} type="file" accept=".pdf,application/pdf" disabled={saving} onChange={(e) => selectFile(e.target.files?.[0])} className="min-w-0 max-w-full" data-testid={`official-kit-file-${type}`} />
        <p className="text-xs text-[#5E6D66]" data-testid={`official-kit-file-limit-${type}`}>PDF non protégé · 10 Mo maximum</p>
        {file && <p className="text-sm break-all" data-testid={`official-kit-selected-file-${type}`}>{file.name} · {(file.size / 1024).toFixed(0)} Ko</p>}
        <div className="space-y-2 min-w-0">
          <Label htmlFor={`official-comment-${type}`} data-testid={`official-kit-comment-label-${type}`}>Commentaire de version {requiresComment ? '(obligatoire)' : '(facultatif)'}</Label>
          <Textarea id={`official-comment-${type}`} value={comment} onChange={(e) => setComment(e.target.value)} rows={3} maxLength={1000} required={requiresComment} aria-required={requiresComment} aria-describedby={`official-comment-help-${type}`} disabled={saving} className="resize-y max-h-40 min-w-0" placeholder="Ex. : mise à jour des conditions d’adhésion." data-testid={`official-kit-comment-${type}`} />
          <div className="flex flex-wrap justify-between gap-2 text-xs text-[#5E6D66]">
            <span id={`official-comment-help-${type}`} data-testid={`official-kit-comment-help-${type}`}>{requiresComment ? 'Motif du changement de version.' : 'Facultatif au premier import.'}</span>
            <span data-testid={`official-kit-comment-count-${type}`}>{comment.length}/1 000</span>
          </div>
        </div>
        <p className="text-sm text-[#5E6D66]" data-testid={`official-kit-version-notice-${type}`}>Cette version s’appliquera aux nouveaux dossiers. Les dossiers existants conserveront leur document.</p>
        {error && <p role="alert" className="text-sm text-red-700" data-testid={`official-kit-upload-error-${type}`}>{error}</p>}
      </div>
      <DialogFooter>
        <Button variant="outline" disabled={saving} onClick={onClose} data-testid={`official-kit-upload-cancel-${type}`}>Annuler</Button>
        <Button disabled={!file || saving || missingComment} onClick={save} className="bg-[#2D5A4C] hover:bg-[#1E4136]" data-testid={`official-kit-upload-submit-${type}`}>
          {saving ? <SpinnerGap size={16} className="mr-2 animate-spin" /> : <UploadSimple size={16} className="mr-2" />}
          {saving ? 'Import en cours…' : 'Mettre en vigueur'}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>;
};